import { Suspense, lazy } from "react";
import { Navigate } from "react-router-dom";

import MainLayout from "../layouts/main";
import HQDashboardLayout from "../layouts/dashboard/toOrderHQ";
import LogoOnlyLayout from "../layouts/LogoOnlyLayout";

import LoadingScreen from "../components/LoadingScreen";
import { Company, Headquarter } from "redux/slices/auth";
import { PATH_LEVEL } from "utils/dataCenter";

const Loadable = (Component: React.ElementType) => (props: any) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks

  return (
    <Suspense fallback={null}>
      <Component {...props} />
    </Suspense>
  );
};

// ----------------본사 시작

// home
const HQHomeSalesAnalytics = Loadable(
  lazy(() => import("../pages/dashboard/toOrderHQ/Home/SalesAnalytics"))
);
const HQHomeSellsAnalytics = Loadable(
  lazy(() => import("../pages/dashboard/toOrderHQ/Home/SellsAnalytics"))
);
// brand
const HQBrandOrderAnalytics = Loadable(
  lazy(() => import("../pages/dashboard/toOrderHQ/Brand/OrderAnalytics"))
);
const HQBrandSalesAnalytics = Loadable(
  lazy(() => import("../pages/dashboard/toOrderHQ/Brand/SalesAnalytics"))
);
const HQBrandSellsAnalytics = Loadable(
  lazy(() => import("../pages/dashboard/toOrderHQ/Brand/SellsAnalytics"))
);
const HQBrandSellsDetailAnalytics = Loadable(
  lazy(() => import("../pages/dashboard/toOrderHQ/Brand/SellsDetailAnalytics"))
);
const HQBrandMapAnalytics = Loadable(
  lazy(() => import("../pages/dashboard/toOrderSC/Home/MapAnalytics"))
);
// store
const HQStoreOrderAnalytics = Loadable(
  lazy(() => import("../pages/dashboard/toOrderHQ/Store/OrdersAnalytics"))
);
const HQStoreSalesAnalytics = Loadable(
  lazy(() => import("../pages/dashboard/toOrderHQ/Store/SalesAnalytics"))
);
const HQStoreSellsAnalytics = Loadable(
  lazy(() => import("../pages/dashboard/toOrderHQ/Store/SellsAnalytics"))
);
const HQStoreSellsDetailAnalytics = Loadable(
  lazy(() => import("../pages/dashboard/toOrderHQ/Store/SellsDetailAnalytics"))
);
const HQStoreReport = Loadable(
  lazy(() => import("../pages/dashboard/toOrderHQ/Report/ReportPage"))
);

// ----------------본사 끝
// Main
const LandingPage = Loadable(lazy(() => import("../pages/LandingPage")));
const ComingSoon = Loadable(lazy(() => import("../pages/ComingSoon")));
const Maintenance = Loadable(lazy(() => import("../pages/Maintenance")));
const Page500 = Loadable(lazy(() => import("../pages/Page500")));
const NotFound = Loadable(lazy(() => import("../pages/Page404")));

export const DEFAULT_PATH = [
  {
    path: "*",
    element: <LogoOnlyLayout />,
    children: [
      { path: "coming-soon", element: <ComingSoon /> },
      { path: "maintenance", element: <Maintenance /> },
      { path: "500", element: <Page500 /> },
      { path: "404", element: <NotFound /> },
      { path: "*", element: <Navigate to="/404" replace /> },
    ],
  },
  {
    path: "/",
    element: <MainLayout />,
    children: [
      {
        path: "/",
        element: <Navigate to="/dashboard/hq_home/hq_salesanalytics" replace />,
      },
    ],
  },
  { path: "*", element: <Navigate to="/404" replace /> },
];

export const getHeadquarterRoutes = (headquarter: Headquarter) => {
  const headPath: any = {
    path: "/",
    element: <HQDashboardLayout />,
    children: [
      {
        path: "/",
        element: <Navigate to="/hq_home/hq_salesanalytics" replace />,
      },
      {
        path: "hq_home",
        children: [
          { path: "hq_salesanalytics", element: <HQHomeSalesAnalytics /> },
          { path: "hq_sellsanalytics", element: <HQHomeSellsAnalytics /> },
        ],
      },
    ],
  };
  const { HEADQUARTER_AUTH, companyObjs } = headquarter;

  let PLUG_IN_MA = false;
  if (companyObjs != null) {
    for (const company of companyObjs) {
      if (company.PLUG_IN_MA) {
        PLUG_IN_MA = true;
        break;
      }
    }
  }
  switch (HEADQUARTER_AUTH?.toString()) {
    case PATH_LEVEL.BASIC:
    case PATH_LEVEL.STANDARD:
    case PATH_LEVEL.PREMIUM:
      headPath.children = [
        ...headPath.children,
        {
          path: "hq_brand",
          children: [
            { path: "hq_salesanalytics", element: <HQBrandSalesAnalytics /> },
            { path: "hq_orderanalytics", element: <HQBrandOrderAnalytics /> },
            { path: "hq_sellsanalytics", element: <HQBrandSellsAnalytics /> },
            {
              path: "hq_sellsdetailanalytics",
              element: <HQBrandSellsDetailAnalytics />,
            },
          ],
        },
        {
          path: "hq_store",
          children: [
            { path: "hq_salesanalytics", element: <HQStoreSalesAnalytics /> },
            { path: "hq_orderanalytics", element: <HQStoreOrderAnalytics /> },
            { path: "hq_sellsanalytics", element: <HQStoreSellsAnalytics /> },
            {
              path: "hq_sellsdetailanalytics",
              element: <HQStoreSellsDetailAnalytics />,
            },
          ],
        },
        {
          path: "hq_report",
          element: <HQStoreReport />,
        },
      ];
      break;
    default:
      headPath.children = [
        ...headPath.children,
        {
          path: "hq_brand",
          children: [
            { path: "hq_salesanalytics", element: <HQBrandSalesAnalytics /> },
            { path: "hq_sellsanalytics", element: <HQBrandSellsAnalytics /> },
            {
              path: "hq_sellsdetailanalytics",
              element: <HQBrandSellsDetailAnalytics />,
            },
          ],
        },
        {
          path: "hq_store",
          children: [
            { path: "hq_salesanalytics", element: <HQStoreSalesAnalytics /> },
            { path: "hq_sellsanalytics", element: <HQStoreSellsAnalytics /> },
            {
              path: "hq_sellsdetailanalytics",
              element: <HQStoreSellsDetailAnalytics />,
            },
          ],
        },
        {
          path: "hq_report",
          element: <HQStoreReport />,
        },
      ];
      break;
  }
  if (PLUG_IN_MA) {
    headPath.children[2].children.push({
      path: "hq_mapanalytics",
      element: <HQBrandMapAnalytics />,
    });
  }
  console.log("headPath : ", headPath);
  return [headPath, ...DEFAULT_PATH];
};
